import { createHabitInstance } from '../models/habit';
import { computeScheduleStats } from '../models/schedule';
import { calculateXP, calculateLevel, BADGE_DEFINITIONS } from '../models/gamification';
import { badgesEarnedBy } from './badges';
/**
 * The streak, advanced for a completion on `todayKey`.
 *
 * Exported because "did the streak move?" is asked in three places and each
 * answer must agree. Rules, in the order they bite:
 *
 * - No previous completion → the streak starts at 1.
 * - Another completion the same day → unchanged. Doing six things on Tuesday
 *   is one day, not six.
 * - Exactly one day later → +1.
 * - A gap → back to 1. Not 0: today still counts.
 * - Earlier than the last completion (backdating a habit, or a device whose
 *   clock disagrees) → hold. Reducing the streak because a completion arrived
 *   out of order would punish the user for a clock.
 */
export function advanceStreak(current, lastCompletionDate, todayKey) {
    if (!lastCompletionDate)
        return 1;
    const last = lastCompletionDate.slice(0, 10);
    if (last === todayKey)
        return Math.max(current, 1);
    const [ly, lm, ld] = last.split('-').map(Number);
    const [ty, tm, td] = todayKey.split('-').map(Number);
    if ([ly, lm, ld, ty, tm, td].some((n) => !Number.isFinite(n)))
        return Math.max(current, 1);
    const days = Math.round((new Date(ty, tm - 1, td).getTime() - new Date(ly, lm - 1, ld).getTime())
        / (1000 * 60 * 60 * 24));
    if (days === 1)
        return current + 1;
    if (days > 1)
        return 1;
    return Math.max(current, 1);
}
/**
 * Apply one completion to a stats object.
 *
 * @param todayKey the LOCAL date the completion is credited to, `YYYY-MM-DD`.
 *   For a habit completed for a past date this is still today — the streak
 *   tracks when you did the work, not which day the work belonged to.
 */
export function applyCompletion(stats, task, todayKey) {
    const xpEarned = calculateXP(task.difficulty, task.estimated_minutes, Boolean(task.is_habit));
    const totalXp = stats.total_xp + xpEarned;
    const level = calculateLevel(totalXp);
    const tasksCompleted = stats.tasks_completed + 1;
    const habitsCompleted = task.is_habit ? stats.habits_completed + 1 : stats.habits_completed;
    const streak = advanceStreak(stats.current_streak, stats.last_completion_date, todayKey);
    const next = {
        ...stats,
        total_xp: totalXp,
        level,
        tasks_completed: tasksCompleted,
        habits_completed: habitsCompleted,
        current_streak: streak,
        longest_streak: Math.max(stats.longest_streak, streak),
        last_completion_date: todayKey,
    };
    // From the one rule table (services/badges.ts), and measured AFTER the
    // level and streak moved — the old check saw the previous level and streak,
    // so those badges arrived one completion late. Facts that need history
    // (perfect days, early bird…) are added by completeTaskEffect.
    const held = new Set(stats.earned_badges);
    const fresh = badgesEarnedBy(next).filter((b) => !held.has(b));
    const now = new Date().toISOString();
    return {
        stats: { ...next, earned_badges: [...stats.earned_badges, ...fresh] },
        xpEarned,
        newBadges: fresh.map((b) => ({ ...BADGE_DEFINITIONS[b], earned_at: now })),
        newLevel: level > stats.level ? level : undefined,
    };
}
/**
 * The HabitInstance a completion should write.
 *
 * The timing is SNAPSHOT onto the instance rather than read back from the
 * habit later, so editing a habit's time next month doesn't retroactively
 * rewrite where it sat on days already past. That rule lived inline in
 * `useTasks.completeTask`; it moved here when the MCP server became a second
 * completer, because "snapshot the slot, fall back to the recurrence time,
 * derive the end if it's missing" is four decisions and two callers making
 * them separately is how they drift apart.
 *
 * @param slot the matching row in that day's plan, when there is one. A habit
 *   completed outside its scheduled slot has none, and falls back to the
 *   habit's own recurrence time.
 */
export function habitInstanceForCompletion(habit, date, { slot } = {}) {
    const startTime = slot?.start_time ?? habit.recurrence?.time_of_day ?? undefined;
    const minutes = slot?.estimated_minutes ?? habit.estimated_minutes ?? undefined;
    let endTime = slot?.end_time;
    if (!endTime && startTime && minutes) {
        const [hours, mins] = startTime.split(':').map(Number);
        const total = hours * 60 + mins + minutes;
        const pad = (n) => String(n).padStart(2, '0');
        endTime = `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
    }
    return {
        ...createHabitInstance(habit.id, date, { instance_detail: 'Completed' }),
        ...(startTime ? { start_time: startTime } : {}),
        ...(endTime ? { end_time: endTime } : {}),
        ...(minutes ? { estimated_minutes: minutes } : {}),
        // Per-instance, not on the habit: a habit is completed on many dates and
        // each is undone separately, so each carries what it paid.
        xp_awarded: calculateXP(habit.difficulty, habit.estimated_minutes, true),
    };
}
/**
 * The day's plan, after a completion is toggled on or off.
 *
 * A completion writes in three stored places, not one: the task (or a
 * HabitInstance), the running stats, and the day's `completed_tasks`. Miss the
 * third and the day's progress bar disagrees with the checkbox next to it —
 * and for a HABIT it is the only place the plan records anything at all,
 * because a habit's status never changes.
 *
 * The summary is RECOMPUTED from the plan rather than incremented. The old
 * incremental version rebuilt its denominator as completed + remaining, so
 * finishing an off-plan task pushed the rate past 100% (7/6 = 117%) and each
 * further completion walked it back down. `computeScheduleStats` keys the
 * denominator to the plan size and caps progress at 100.
 *
 * @returns the updated schedule, or `null` when it is already in that state —
 *   so a caller can skip a pointless write.
 */
export function scheduleAfterCompletion(schedule, taskId, done) {
    const current = schedule.completed_tasks ?? [];
    const already = current.includes(taskId);
    if (done === already)
        return null;
    const completed = done ? [...current, taskId] : current.filter((id) => id !== taskId);
    const stats = computeScheduleStats({
        scheduled_tasks: schedule.scheduled_tasks,
        completed_tasks: completed,
    });
    return {
        ...schedule,
        completed_tasks: completed,
        summary: {
            ...schedule.summary,
            tasks_completed: stats.completed,
            tasks_remaining: stats.remaining,
            completion_rate: stats.progress,
        },
    };
}
//# sourceMappingURL=completion.js.map